import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import {
  isChunkLoadError,
  isNetworkError,
  isReloadingAfterChunkLoadError,
} from '@welma-education/common';
import { ToastService } from '@welma-education/ui/sonner';

const NETWORK_ERROR_TOAST_ID = 'network-error';
const NEW_VERSION_TOAST_ID = 'new-version';

/**
 * Reports unhandled errors to Sentry, except for:
 * - network errors (connection lost, server unreachable): they are not bugs,
 *   the user is notified instead and they are logged as warnings;
 * - chunk load errors (stale chunk after a deployment): the user is invited
 *   to refresh the page to get the new version of the application. Failed
 *   navigations are already recovered by `withChunkLoadErrorRecovery`, this
 *   covers the other cases (chunks loaded outside of a navigation, or a
 *   navigation failing again right after a reload).
 */
@Injectable()
export class AppErrorHandler implements ErrorHandler {
  private readonly sentryErrorHandler = Sentry.createErrorHandler();
  // Services are resolved lazily: the error handler is created before them.
  private readonly injector = inject(Injector);

  handleError(error: unknown): void {
    if (isNetworkError(error) || (isChunkLoadError(error) && !navigator.onLine)) {
      console.warn('Network error', error);
      this.showToast(NETWORK_ERROR_TOAST_ID, 'COMMON.ERROR.NETWORK');
      return;
    }

    if (isChunkLoadError(error)) {
      if (isReloadingAfterChunkLoadError()) {
        return;
      }
      console.warn('Chunk load error', error);
      this.showToast(NEW_VERSION_TOAST_ID, 'COMMON.NEW_VERSION', {
        duration: Infinity,
        action: 'COMMON.REFRESH',
      });
      return;
    }

    this.sentryErrorHandler.handleError(error);
  }

  private showToast(
    id: string,
    key: string,
    { duration, action }: { duration?: number; action?: string } = {},
  ): void {
    try {
      const translate = this.injector.get(TranslateService);
      // Reusing the same id updates the toast instead of stacking new ones.
      this.injector.get(ToastService).error(translate.instant(key), {
        id,
        description: translate.instant(`${key}.DESCRIPTION`),
        ...(duration !== undefined && { duration }),
        ...(action && {
          action: { label: translate.instant(action), onClick: () => window.location.reload() },
        }),
      });
    } catch {
      // The toast is best effort, e.g. the application may not be bootstrapped yet.
    }
  }
}
